import { jwtDecode } from 'jwt-decode';
import { posthog } from './analytics';
import { getKeyValue, removeKeyValue } from './kvstore'; // storeKeyValue
// Synchronous fast check - only checks localStorage/sessionStorage (no indexedDB/cookie parsing)
// Used to quickly determine if user might be logged in before async checks complete
export const hasSessionSync = (key) => {
    if (typeof window === 'undefined')
        return false;
    try {
        const session = sessionStorage.getItem(key);
        if (session)
            return true;
    }
    catch {
        /* ignore */
    }
    try {
        const local = localStorage.getItem(key);
        if (local)
            return true;
    }
    catch {
        /* ignore */
    }
    return false;
};
// const DEBUG = false;
// function log(msg: string): void {
// 	if (DEBUG) console.log(msg);
// }
const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));
const MAX_AGE = 60 * 60 * 24 * 400; // 34560000 // 400 days (max age permitted by Safari in 2024)
// returns whether a string is a valid JWT, regardless of its contents, expiration date and signature validity
export const isValidJWT = (jwt) => {
    let decoded = null;
    try {
        decoded = jwtDecode(jwt);
        if (!decoded)
            throw 'no decoded jwt';
        if (!decoded.id)
            throw 'no id in jwt';
        if (!decoded.iat)
            throw 'no iat in jwt';
        if (!decoded.exp)
            throw 'no exp in jwt';
        return [true, decoded];
    }
    catch (e) {
        console.log(`Error decoding jwt: ${e}: [${jwt}]`);
        posthog.capture(`Error decoding jwt`, { error: e });
    }
    return [false, decoded];
};
// stores a JWT token in session, local, cookie and indexedDb storage
export const storeJwt = async (key, value) => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined')
        return false;
    const [valid, decoded] = isValidJWT(value);
    if (!valid) {
        console.log(`tried to store invalid jwt: ${value} ${JSON.stringify(decoded)}`);
        posthog.capture(`tried to store invalid jwt`);
        return false;
    }
    let success = false;
    try {
        sessionStorage.setItem(key, value);
        success = true;
    }
    catch (e) {
        console.log(`Error storing ${key} in session storage: ${e}`);
        posthog.capture(`Error storing in session storage`, { key, error: e });
    }
    try {
        localStorage.setItem(key, value);
        success = true;
    }
    catch (e) {
        console.log(`Error storing ${key} in local storage: ${e}`);
        posthog.capture(`Error storing in local storage`, { key, error: e });
    }
    // don't use async storage until we implement logout and deprecate v1 auth
    // try {
    // 	const success2 = await storeKeyValue(key, value);
    // 	success = success || success2;
    // } catch (e) {
    // 	console.log(`Error storing ${key} in indexedDb: ${e}`);
    // 	posthog.capture(`Error storing in indexedDb`, { key, error: e });
    // }
    try {
        document.cookie = `__HOST-${key}=${value}; path=/; Max-Age=${MAX_AGE}; SameSite=Strict; Secure;`;
        success = true;
    }
    catch (e) {
        console.log(`Error storing ${key} as cookie: ${e}`);
        posthog.capture(`Error storing in cookie`, { key, error: e });
    }
    return success;
};
// gets the stored JWT token from session, local, cookie and indexedDb storage
// with the most recent issuance date
export const getJwt = async (key) => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined')
        return [null, null, null];
    let jwt = null;
    let token = null;
    let tokenLocation = null; // 1 = sessionStorage, 2 = localStorage, 3 = indexedDb, 4 = cookie
    try {
        const str = sessionStorage.getItem(key);
        if (str) {
            const [valid, decoded] = isValidJWT(str);
            if (valid && decoded) {
                jwt = str;
                token = decoded;
                tokenLocation = 1;
            }
        }
    }
    catch (e) {
        console.log(`Error getting ${key} from session storage: ${e}`);
        posthog.capture(`Error getting from session storage`, { key, error: e });
    }
    try {
        const str = localStorage.getItem(key);
        if (str) {
            const [valid, decoded] = isValidJWT(str);
            if (valid && decoded) {
                if (token && token.id !== decoded.id) {
                    console.log(`found tokens for both ${token.id} and ${decoded.id}`);
                }
                jwt = str;
                token = decoded;
                tokenLocation = 2;
            }
        }
    }
    catch (e) {
        console.log(`Error getting ${key} from local storage: ${e}`);
        posthog.capture(`Error getting from local storage`, { key, error: e });
    }
    try {
        const str = await getKeyValue(key);
        if (str) {
            const [valid, decoded] = isValidJWT(str);
            if (valid && decoded) {
                if (token && token.id !== decoded.id) {
                    console.log(`found tokens for both ${token.id} and ${decoded.id}`);
                }
                jwt = str;
                token = decoded;
                tokenLocation = 3;
            }
        }
    }
    catch (e) {
        console.log(`Error getting ${key} from indexedDb storage: ${e}`);
        posthog.capture(`Error getting from session indexedDb`, { key, error: e });
    }
    try {
        let data = window.document.cookie ?? '';
        if (data.startsWith('?'))
            data = data.slice(1);
        const items = data.split('; ');
        const item = items.find((p) => p.startsWith(`__HOST-${key}=`));
        const str = item?.split('=')[1];
        if (str) {
            const [valid, decoded] = isValidJWT(str);
            if (valid && decoded) {
                if (token && token.id !== decoded.id) {
                    console.log(`found tokens for both ${token.id} and ${decoded.id}`);
                }
                jwt = str;
                token = decoded;
                tokenLocation = 4;
            }
        }
    }
    catch (e) {
        console.log(`Error getting ${key} from cookies: ${e}`);
        posthog.capture(`Error getting from cookie`, { key, error: e });
    }
    return [jwt, token, tokenLocation];
};
// removes the stored JWT token from session, local, cookie and indexedDb storage
export const clearJwt = async (key) => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined')
        return;
    try {
        sessionStorage.removeItem(key);
    }
    catch (e) {
        console.log(`Error removing ${key} from session storage: ${e}`);
        posthog.capture(`Error removing from session storage`, { key, error: e });
    }
    try {
        localStorage.removeItem(key);
    }
    catch (e) {
        console.log(`Error removing ${key} from local storage: ${e}`);
        posthog.capture(`Error removing from local storage`, { key, error: e });
    }
    try {
        await removeKeyValue(key);
    }
    catch (e) {
        console.log(`Error getting ${key} from indexedDb storage: ${e}`);
        posthog.capture(`Error removing from indexedDb`, { key, error: e });
    }
    try {
        document.cookie = `${key}=foobar; path=/; Max-Age=-99999999;`;
    }
    catch (e) {
        console.log(`Error removing ${key} from cookies: ${e}`);
        posthog.capture(`Error removing from cookie`, { key, error: e });
    }
    try {
        document.cookie = `${key}=foobar; Max-Age=-99999999;`;
    }
    catch (e) {
        console.log(`Error removing ${key} from cookies: ${e}`);
        posthog.capture(`Error removing from cookie`, { key, error: e });
    }
    try {
        document.cookie = `${key}=foobar; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
    catch (e) {
        console.log(`Error removing ${key} from cookies: ${e}`);
        posthog.capture(`Error removing from cookie`, { key, error: e });
    }
    try {
        document.cookie = `__HOST-${key}=foobar; path=/; Max-Age=-99999999; SameSite=Strict; Secure;`;
    }
    catch (e) {
        console.log(`Error removing ${key} from cookies: ${e}`);
        posthog.capture(`Error removing from cookie storage`, { key, error: e });
    }
    await sleep(20);
    const [jwt, token] = await getJwt(key);
    if (jwt || token) {
        console.log(`Error clearing Jwt from storage`);
        posthog.capture(`Error clearing Jwt from storage`);
    }
};
// gets the stored JWT from session, local, cookie and indexedDb storage
// and restores (refreshes) it into all storage locations
export const refreshJwt = async (key) => {
    const [jwt, token, tokenLocation] = await getJwt(key);
    if (jwt && token)
        await storeJwt(key, jwt);
    return [jwt, token, tokenLocation];
};
